import {
  type AssessmentSelectDto,
  AssessmentSubtype,
  AssessmentType,
  type CyberAssessmentAnswerSelectDto,
  type CyberAssessmentApplicabilityQuestionSelectDto,
  type CyberAssessmentQuestionGroupSelectDto,
  CyberAssessmentQuestionKind,
  type CyberAssessmentQuestionSelectDto,
  type CyberAssessmentQuestionnaireSnapshotSelectDto,
  type CyberAssessmentSelectDto,
  CyberAssessmentStatus,
  SurveyStatus,
} from '@@types/api.types';

type CyberAssessmentSourceLike = Pick<AssessmentSelectDto, 'createdAt' | 'id' | 'subtype' | 'surveyStatus' | 'type'>;

export type CyberAssessmentSidebarAnswerLike = Pick<CyberAssessmentAnswerSelectDto, 'questionId' | 'response'>;

export type CyberAssessmentSidebarQuestionLike = Pick<CyberAssessmentQuestionSelectDto, 'id'>;

export type CyberAssessmentSidebarQuestionGroupLike = {
  questions: CyberAssessmentSidebarQuestionLike[];
};

export type CyberAssessmentSidebarAssessmentLike = Pick<CyberAssessmentSelectDto, 'id' | 'updatedAt'> & {
  answers?: CyberAssessmentSidebarAnswerLike[];
  questionnaireSnapshot: CyberAssessmentQuestionnaireLike;
  status: CyberAssessmentStatus;
};

export type CyberAssessmentDraftProgress = {
  answeredQuestions: number;
  progress: number;
  totalQuestions: number;
};

export type CyberAssessmentSidebarModel<TAssessment extends CyberAssessmentSidebarAssessmentLike> = {
  draftAssessment: TAssessment | null;
  draftProgress: CyberAssessmentDraftProgress | null;
  historyAssessments: TAssessment[];
  selectedAssessment: TAssessment | null;
};

type CyberAssessmentAnswerLike = Partial<Pick<CyberAssessmentAnswerSelectDto, 'cyberAssessmentId' | 'id'>> &
  Pick<CyberAssessmentAnswerSelectDto, 'questionId' | 'response'>;

type CyberAssessmentApplicabilityQuestionLike = Pick<
  CyberAssessmentApplicabilityQuestionSelectDto,
  'applicabilityQuestionIds' | 'description' | 'id' | 'prompt' | 'sectionId'
>;

export type CyberAssessmentQuestionLike = Pick<
  CyberAssessmentQuestionSelectDto,
  'applicabilityQuestionIds' | 'explanation' | 'id' | 'missionImpactIfNo' | 'prompt' | 'section' | 'sectionId'
> &
  Partial<
    Pick<CyberAssessmentQuestionSelectDto, 'isDomainElevated' | 'kind' | 'purpleTeamExercise' | 'sourceScrdfDomainId'>
  >;

type CyberAssessmentQuestionGroupLike = Pick<
  CyberAssessmentQuestionGroupSelectDto,
  'id' | 'questionCount' | 'title'
> & {
  questions: CyberAssessmentQuestionLike[];
};

type CyberAssessmentQuestionnaireLike = Pick<
  CyberAssessmentQuestionnaireSnapshotSelectDto,
  'applicabilityQuestions' | 'frameworkVersion'
> & {
  questionGroups: CyberAssessmentQuestionGroupLike[];
};

type CyberAssessmentLike = Pick<CyberAssessmentSelectDto, 'id'> & {
  answers?: CyberAssessmentAnswerLike[];
  questionnaireSnapshot: CyberAssessmentQuestionnaireLike;
};

export type CyberAssessmentShellSection = {
  answered: number;
  id: string;
  questions: CyberAssessmentQuestionLike[];
  questionIds: string[];
  title: string;
  total: number;
};

export type CyberAssessmentShellChapter = {
  id: string;
  sections: CyberAssessmentShellSection[];
  title: string;
};

export type CyberAssessmentShellModel = {
  answeredQuestions: number;
  chapters: CyberAssessmentShellChapter[];
  firstUnansweredSectionId: string | null;
  progress: number;
  questionOrder: Array<{ questionId: string; sectionId: string }>;
  sectionOrder: string[];
  sectionsById: Record<string, CyberAssessmentShellSection>;
  totalQuestions: number;
};

export const getCompletedMilitaryTrlSourceAssessments = <T extends CyberAssessmentSourceLike>(
  assessments: T[],
): T[] => {
  return assessments
    .filter(
      (assessment) =>
        assessment.type === AssessmentType.TRL &&
        assessment.subtype === AssessmentSubtype.MILITARY &&
        assessment.surveyStatus === SurveyStatus.COMPLETE,
    )
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
};

export const getDefaultCompletedMilitaryTrlSourceAssessmentId = <T extends CyberAssessmentSourceLike>(
  assessments: T[],
): string | null => {
  return getCompletedMilitaryTrlSourceAssessments(assessments)[0]?.id ?? null;
};

export const getCyberAssessmentDraftProgress = <TAssessment extends CyberAssessmentSidebarAssessmentLike>(
  assessment: TAssessment,
): CyberAssessmentDraftProgress => {
  const shellModel = buildCyberAssessmentShellModel(assessment);
  const answeredQuestions = shellModel.answeredQuestions;
  const progress = getCyberAssessmentShellProgress(shellModel);
  const totalQuestions = shellModel.totalQuestions;

  return {
    answeredQuestions,
    progress,
    totalQuestions,
  };
};

export const buildCyberAssessmentAnswerResponseByScrdfCode = (
  answers: CyberAssessmentAnswerLike[] = [],
): Map<string, string> => {
  return new Map(answers.map((answer) => [answer.questionId, answer.response?.trim() ?? '']));
};

export const findCyberAssessmentAnswerByScrdfCode = <TAnswer extends CyberAssessmentAnswerLike>(params: {
  answers?: TAnswer[];
  scrdfCode?: string;
}): TAnswer | undefined => {
  const { answers, scrdfCode } = params;

  if (!scrdfCode) {
    return undefined;
  }

  return answers?.find((answer) => answer.questionId === scrdfCode);
};

export const buildCyberAssessmentSidebarModel = <TAssessment extends CyberAssessmentSidebarAssessmentLike>(params: {
  assessments: TAssessment[];
  selectedAssessmentId: string | null;
}): CyberAssessmentSidebarModel<TAssessment> => {
  const { assessments, selectedAssessmentId } = params;

  const draftAssessment = assessments.find((assessment) => assessment.status === CyberAssessmentStatus.DRAFT) ?? null;
  const historyAssessments = assessments.filter((assessment) => assessment.status !== CyberAssessmentStatus.DRAFT);
  const selectedAssessment =
    historyAssessments.find((assessment) => assessment.id === selectedAssessmentId) ?? historyAssessments[0] ?? null;

  return {
    draftAssessment,
    draftProgress: draftAssessment ? getCyberAssessmentDraftProgress(draftAssessment) : null,
    historyAssessments,
    selectedAssessment,
  };
};

export const isCyberAssessmentQuestionnaireEmpty = (cyberAssessment: CyberAssessmentLike): boolean => {
  return (
    cyberAssessment.questionnaireSnapshot.applicabilityQuestions.length === 0 &&
    cyberAssessment.questionnaireSnapshot.questionGroups.every((questionGroup) => questionGroup.questions.length === 0)
  );
};

export const createCyberAssessmentSectionId = (params: { groupId: string; sectionKey: string }): string => {
  const { groupId, sectionKey } = params;

  return `${groupId}:${sectionKey}`;
};

export const getCyberAssessmentChapterProgress = (chapter: CyberAssessmentShellChapter): number => {
  if (chapter.sections.length === 0) {
    return 0;
  }

  return (
    chapter.sections.reduce((accumulator, section) => {
      if (section.total === 0) {
        return accumulator;
      }

      return accumulator + section.answered / section.total;
    }, 0) / chapter.sections.length
  );
};

export const getCyberAssessmentShellProgress = (shellModel: CyberAssessmentShellModel): number => {
  if (shellModel.chapters.length === 0) {
    return 0;
  }

  return (
    shellModel.chapters.reduce((accumulator, chapter) => {
      return accumulator + getCyberAssessmentChapterProgress(chapter);
    }, 0) / shellModel.chapters.length
  );
};

export const resolveCyberAssessmentQuestionIndex = (params: {
  questionCount: number;
  requestedIndex: number;
}): number => {
  const { questionCount, requestedIndex } = params;

  if (questionCount <= 0) {
    return 0;
  }

  if (requestedIndex < 0) {
    return 0;
  }

  if (requestedIndex >= questionCount) {
    return questionCount - 1;
  }

  return requestedIndex;
};

export const resolveDisplayedCyberAssessmentQuestionIndex = (params: {
  currentSectionId: string;
  defaultQuestionIndex: number;
  questionCount: number;
  stateQuestionIndex: number;
  stateSectionId?: string;
}): number => {
  const { currentSectionId, defaultQuestionIndex, questionCount, stateQuestionIndex, stateSectionId } = params;

  if (stateSectionId !== currentSectionId) {
    return resolveCyberAssessmentQuestionIndex({
      questionCount,
      requestedIndex: defaultQuestionIndex,
    });
  }

  return resolveCyberAssessmentQuestionIndex({
    questionCount,
    requestedIndex: stateQuestionIndex,
  });
};

const normalizeSectionKey = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '');
};

export const buildCyberAssessmentShellModel = (cyberAssessment: CyberAssessmentLike): CyberAssessmentShellModel => {
  const answerResponseByScrdfCode = buildCyberAssessmentAnswerResponseByScrdfCode(cyberAssessment.answers ?? []);

  let answeredQuestions = 0;
  let firstUnansweredSectionId: string | null = null;
  const questionOrder: Array<{ questionId: string; sectionId: string }> = [];
  const sectionOrder: string[] = [];
  const sectionsById: Record<string, CyberAssessmentShellSection> = {};
  const chapters: CyberAssessmentShellChapter[] = [];

  const visibleApplicabilityQuestions = (cyberAssessment.questionnaireSnapshot.applicabilityQuestions ?? []).filter(
    (question) =>
      (question.applicabilityQuestionIds ?? []).every(
        (applicabilityQuestionId) => answerResponseByScrdfCode.get(applicabilityQuestionId) === 'YES',
      ),
  );

  if (visibleApplicabilityQuestions.length > 0) {
    const applicabilitySectionId = createCyberAssessmentSectionId({
      groupId: 'applicability',
      sectionKey: normalizeSectionKey('General'),
    });
    const applicabilityQuestions = visibleApplicabilityQuestions.map((question) => ({
      explanation: question.description,
      id: question.id,
      isDomainElevated: false,
      kind: CyberAssessmentQuestionKind.Control,
      missionImpactIfNo: '',
      prompt: question.prompt,
      section: 'General',
      sectionId: question.sectionId,
    }));
    const answeredApplicabilityQuestions = applicabilityQuestions.filter((question) =>
      Boolean(answerResponseByScrdfCode.get(question.id)),
    ).length;

    if (answeredApplicabilityQuestions < applicabilityQuestions.length) {
      firstUnansweredSectionId = applicabilitySectionId;
    }

    sectionOrder.push(applicabilitySectionId);
    questionOrder.push(
      ...applicabilityQuestions.map((question) => ({
        questionId: question.id,
        sectionId: applicabilitySectionId,
      })),
    );
    sectionsById[applicabilitySectionId] = {
      answered: answeredApplicabilityQuestions,
      id: applicabilitySectionId,
      questionIds: applicabilityQuestions.map((question) => question.id),
      questions: applicabilityQuestions,
      title: 'General',
      total: applicabilityQuestions.length,
    };
    answeredQuestions += answeredApplicabilityQuestions;
    chapters.push({
      id: 'applicability',
      sections: [sectionsById[applicabilitySectionId]],
      title: 'Assessment Context',
    });
  }

  const questionGroups = cyberAssessment.questionnaireSnapshot.questionGroups.map((questionGroup) => {
    const sectionsByKey = new Map<string, CyberAssessmentShellSection>();

    for (const question of questionGroup.questions) {
      const isVisible = (question.applicabilityQuestionIds ?? []).every(
        (applicabilityQuestionId) => answerResponseByScrdfCode.get(applicabilityQuestionId) === 'YES',
      );

      if (!isVisible) {
        continue;
      }

      const sectionTitle = question.section?.trim() || question.prompt;
      const sectionId =
        question.sectionId ??
        createCyberAssessmentSectionId({
          groupId: questionGroup.id,
          sectionKey: normalizeSectionKey(sectionTitle),
        });
      const isAnswered = Boolean(answerResponseByScrdfCode.get(question.id));

      if (!sectionsByKey.has(sectionId)) {
        sectionOrder.push(sectionId);
        sectionsByKey.set(sectionId, {
          answered: 0,
          id: sectionId,
          questionIds: [],
          questions: [],
          title: sectionTitle,
          total: 0,
        } satisfies CyberAssessmentShellSection);
      }

      const section = sectionsByKey.get(sectionId);

      if (!section) {
        continue;
      }

      if (isAnswered) {
        answeredQuestions += 1;
        section.answered += 1;
      } else if (!firstUnansweredSectionId) {
        firstUnansweredSectionId = sectionId;
      }

      section.questionIds.push(question.id);
      section.questions.push(question);
      section.total += 1;
      questionOrder.push({ questionId: question.id, sectionId });
      sectionsById[section.id] = section;
    }

    return {
      id: questionGroup.id,
      sections: Array.from(sectionsByKey.values()),
      title: questionGroup.title,
    } satisfies CyberAssessmentShellChapter;
  });
  chapters.push(...questionGroups.filter((chapter) => chapter.sections.length > 0));

  const totalQuestions = questionOrder.length;

  return {
    answeredQuestions,
    chapters,
    firstUnansweredSectionId,
    progress: totalQuestions === 0 ? 1 : answeredQuestions / totalQuestions,
    questionOrder,
    sectionOrder,
    sectionsById,
    totalQuestions,
  };
};
